import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PageHeader } from '../components/shared/PageHeader';
import { ErrorAlert, SuccessAlert } from '../components/shared/AlertMessages';
import { TelegramConfigSection } from '../components/settings/TelegramConfigSection';
import { BackupSection } from '../components/settings/BackupSection';
import { MaintenanceSection } from '../components/settings/MaintenanceSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const [config, setConfig] = useState({ bot_token: '', channel_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [reregistering, setReregistering] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/telegram/config`, { withCredentials: true });
      if (res.data.configured) {
        setConfig({ bot_token: '***configurato***', channel_id: res.data.channel_id || '' });
      }
    } catch {
      // Config not yet set, ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = { ...config, app_base_url: window.location.origin };
      const res = await axios.post(`${API_URL}/api/telegram/config`, payload, { withCredentials: true });
      const data = res.data;
      if (data.webhook_ok) {
        setSuccess(`Configurazione salvata. Webhook registrato: ${data.webhook_url}`);
        setWebhookInfo({ url: data.webhook_url, ok: true });
      } else {
        setSuccess('Configurazione salvata, ma il webhook potrebbe non essere stato registrato correttamente.');
        setWebhookInfo({ url: data.webhook_url, ok: false });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleReregister = async () => {
    setReregistering(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/api/telegram/reregister-webhook`, { app_base_url: window.location.origin }, { withCredentials: true });
      setSuccess(res.data.message + ` (${res.data.webhook_url})`);
      setWebhookInfo({ url: res.data.webhook_url, ok: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella ri-registrazione del webhook');
    } finally { setReregistering(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/telegram/test`, {}, { withCredentials: true });
      setTestResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel test di connessione');
    } finally { setTesting(false); }
  };

  const handleRecover = async () => {
    setRecovering(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/api/telegram/recover`, {}, { withCredentials: true });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel recupero messaggi');
    } finally { setRecovering(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <PageHeader title="Impostazioni" />

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <ErrorAlert message={error} onClose={() => setError('')} />
        <SuccessAlert message={success} onClose={() => setSuccess('')} />

        <TelegramConfigSection
          config={config} setConfig={setConfig}
          saving={saving} testing={testing} reregistering={reregistering} recovering={recovering}
          testResult={testResult} webhookInfo={webhookInfo}
          onSave={handleSave} onTest={handleTest} onReregister={handleReregister} onRecover={handleRecover}
        />

        <BackupSection onSuccess={setSuccess} onError={setError} />
        <MaintenanceSection onSuccess={setSuccess} onError={setError} />
      </main>
    </div>
  );
};

export default SettingsPage;
